import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DealerAuthProvider } from '../context/DealerAuthContext';
import { useTheme } from '@/context/ThemeContext';
import DealerLayout from '../components/DealerLayout';
import { useProductCatalog } from '@/context/ProductCatalogContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

const catColor: Record<string, string> = {
  lithium: 'bg-[#F97316]/20 text-[#F97316]',
  agm: 'bg-green-500/20 text-green-400',
  gel: 'bg-yellow-500/20 text-yellow-400',
};

function DealerProductsContent() {
  const { theme } = useTheme();
  const { products } = useProductCatalog();
  const { settings } = useBadgeVisibility();
  const dark = theme === 'dark';
  const [catFilter, setCatFilter] = useState<'all' | 'lithium' | 'agm' | 'gel'>('all');
  const filtered = catFilter === 'all' ? products : products.filter((p) => p.category === catFilter);

  const card = dark ? 'bg-[#1C2B3A] border-white/5' : 'bg-white border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/40' : 'text-gray-400';
  const imgBg = dark ? 'bg-[#0D1B2A]' : 'bg-gray-100';
  const inactiveFilter = dark ? 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:text-gray-900 hover:bg-gray-200';

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {(['all', 'lithium', 'agm', 'gel'] as const).map((cat) => (
          <button key={cat} onClick={() => setCatFilter(cat)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${catFilter === cat ? 'bg-[#F97316] text-white' : inactiveFilter}`}>
            {cat === 'all' ? 'All Products' : cat === 'lithium' ? 'Lithium LiFePO4' : cat === 'agm' ? 'AGM Series' : 'Gel Series'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((product) => (
          <div key={product.id} className={`border rounded-lg overflow-hidden group ${card}`}>
            <div className={`relative w-full h-44 overflow-hidden ${imgBg}`}>
              <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              {settings.productBadges && product.badge && (
                <span className="absolute top-2 left-2 bg-[#F97316] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full">
                  {product.badge}
                </span>
              )}
            </div>
            <div className="p-4">
              <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full capitalize mb-2 ${catColor[product.category]}`}>
                {product.category}
              </span>
              <h3 className={`font-bold text-sm mb-1 leading-tight ${text}`}>{product.name}</h3>
              <p className={`text-xs mb-3 ${muted}`}>{product.capacity}Ah &bull; {product.voltage}V &bull; {product.weight} lbs</p>
              <div className="flex items-center justify-between">
                <p className="text-[#F97316] font-black text-base">${product.price.toLocaleString()}</p>
                <Link to="/dealer/new-order"
                  className="flex items-center gap-1 bg-[#F97316]/20 text-[#F97316] hover:bg-[#F97316] hover:text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-add-line text-xs"></i>
                  </div>
                  Order
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DealerProductsPage() {
  return (
    <DealerAuthProvider>
      <DealerLayout title="Products">
        <DealerProductsContent />
      </DealerLayout>
    </DealerAuthProvider>
  );
}
